import { useState } from 'react';

import * as Sentry from '@sentry/browser';
import { Spinner } from '@blueprintjs/core';
import {
  Alert,
  Button,
  createStyles,
  Divider,
  makeStyles,
  PhotoRow,
  PhotoUpload,
  TextField,
} from '@opendoor/mosaic';
import { WorkOrderMessages, WorkOrderMessagesProvider } from '@opendoor/tessellation';

import { usePostNotesAndPhotos } from '../../hooks/usePostNotesAndPhotos';

interface NotesProps {
  title: string;
  placeHolderText?: string;
  needsUpdate: boolean;
  workOrderId: string;
  isOnsite: boolean;
}

const Notes = ({ title, placeHolderText, needsUpdate, workOrderId, isOnsite }: NotesProps) => {
  const styles = useStyles();
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([] as File[]);
  const placeHolder = placeHolderText ?? 'Add any comment here';

  const [postNotesAndPhotos] = usePostNotesAndPhotos();

  const removePhoto = (index: number) => {
    const newPhotos = [...files];
    newPhotos.splice(index, 1);
    setFiles(newPhotos);
  };

  const onSubmitNotes = async () => {
    setLoading(true);

    try {
      const status = await postNotesAndPhotos(workOrderId, newMessage, files, isOnsite);
      if (status) {
        setNewMessage('');
        setFiles([]);
        needsUpdate = false;
      }
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setTag('source', 'Notes');
        Sentry.captureException(error);
      });
    }

    setLoading(false);
  };

  const submitButton = () => {
    return (
      <Button
        color="primary"
        variant="contained"
        onClick={onSubmitNotes}
        disabled={!newMessage || loading}
        className={styles.submitButton}
      >
        {loading ? <Spinner /> : 'Send'}
      </Button>
    );
  };

  return (
    <div className={styles.notesContainer}>
      <span className={styles.title}>{title}</span>
      {needsUpdate ? (
        <Alert severity="error" variant="standard" className={styles.alertBox}>
          No Progress uploaded since 48 hrs
        </Alert>
      ) : null}
      <TextField
        data-testid="tp-notes-text-field"
        placeholder={placeHolder}
        minRows={3}
        maxRows={4}
        value={newMessage}
        onChange={(e) => setNewMessage(e.target.value)}
        multiline
        fullWidth
        className="mt-5"
      />
      <div className="mt-4 inline-flex">
        <PhotoUpload
          style={{ height: '80px', width: '80px', marginRight: '8px' }}
          onSave={(uploadedFiles: File[]) => {
            const tempFiles = [...files, ...uploadedFiles];
            setFiles(tempFiles);
          }}
          className="pa-6"
        />
        <PhotoRow
          thumbnailHeight={80}
          thumbnailWidth={80}
          photoUrls={files.map((file) => {
            return URL.createObjectURL(file);
          })}
          onRemovePhoto={removePhoto}
        />
      </div>
      {submitButton()}
      <Divider orientation="horizontal" className="mt-5" />
      <div className="mt-3">
        <WorkOrderMessagesProvider
          workOrderId={workOrderId ?? ''}
          isOnSite={isOnsite}
          loading={loading}
        >
          <WorkOrderMessages />
        </WorkOrderMessagesProvider>
      </div>
    </div>
  );
};

const useStyles = makeStyles(() =>
  createStyles({
    notesContainer: {
      width: '49%',
      background: 'white',
      padding: '1.2rem',
      '@media (max-width: 768px)': {
        width: '100%',
      },
    },
    title: {
      fontWeight: 600,
      fontSize: 24,
      fontFamily: 'Graphik',
      color: 'black',
    },
    submitButton: {
      float: 'right',
      marginTop: '8px',
      borderRadius: '4px',
      padding: '12px 16px',
      fontSize: '14px',
      fontWeight: 500,
      width: '80px',
      height: '40px',
    },
    photoUpload: {
      height: '80px',
      width: '80px',
      marginRight: '4px',
    },
    alertBox: {
      fontSize: '12px',
      fontWeight: 600,
      float: 'right',
      '@media (max-width: 481px)': {
        display: 'flex',
        width: '100%',
      },
    },
  }),
);

export default Notes;
